<template>
	<Row>
		<Col :span="24">
			<PaasCoordsChart
				:minDate="minDate"
				:maxDate="maxDate"
				@corrected="onCorrected"
			/>
			<Row :gutter="[16, 16]" class="pd-md-vertical">
				<Col :span="24">
					<StatsChartTitleWithDescription
						titleKey="paas.prevResults"
					/>
					<Spin v-if="store.isLoading && paasTasks.length === 0" :spinning="true">
						<div class="h-500"></div>
					</Spin>
					<div v-if="store.showProgress" class="pd-lg-x">
						<Badge v-if="store.finishedCount < store.totalCount" status="processing" :text="$t('paas.progressHint')" />
						<Descriptions v-if="store.lastStart" size="small" :column="1">
							<DescriptionsItem :label="$t('paas.lastStart')">{{ store.lastStart }}</DescriptionsItem>
						</Descriptions>
						<DiscreteProgress :count="store.finishedCount" :total="store.totalCount" />
					</div>
					<Result
						v-if="!store.showProgress && !store.isLoading && paasTasks.length === 0"
						:title="$t('paas.noDataTitle')"
						:sub-title="$t('paas.noDataSubTitle')"
					>
						<template #icon>
							<FieldTimeOutlined />
						</template>
					</Result>
				</Col>
				<Col v-if="paasTasks.length > 0" :span="24">
					<div class="scrollable-x">
						<Table
							:columns="columns"
							:data-source="paasTasks"
							:loading="store.isLoading"
							:showHeader="false"
							:pagination="{ defaultPageSize: 10, position: ['bottomRight'] }"
						>
							<template #bodyCell="{ column, record }">
								<template v-if="column.key === 'createdStr'">
									<NoLineBreaks>{{ record.createdStr }}</NoLineBreaks>
								</template>
								<template v-else-if="column.key === 'status'">
									<StatusDisplay
										:source="record"
										field="status"
										prefix="paas.statuses"
										:badgeDict="PAAS_TASK_STATUS_BADGES"
									/>
								</template>
								<template v-else-if="column.key === 'mountpoints'">
									<PaasMountpointsDisplay :mountpoints="record.mountpoints" :successMountpointsCount="record.success_mountpoints_count" />
								</template>
								<template v-else-if="column.key === 'actions'">
									<Space>
										<Button :type="record.applyButtonType" @click="onReviewAndApply(record.id)">{{ $t('paas.buttons.' + record.status) }}</Button>
										<Button v-if="['done', 'failed'].includes(record.status)" @click="onIgnore(record.id)">{{ $t('paas.buttons.ignore') }}</Button>
									</Space>
								</template>
							</template>
						</Table>
					</div>
				</Col>
			</Row>
			<div class="flex-wrapper" v-if="sessionStore.debug">
				<div class="flex-grow" />
				<PopconfirmButton
					:title="$t('common.dangerousOperationLabel')"
					:ok-text="$t('common.confirmButtonLabel')"
					:cancel-text="$t('common.cancelButtonLabel')"
					:onConfirm="onCreateTestData"
					:disabled="isProcessingTestData"
					isDangerousOperation
				>
					{{ $t('paas.buttons.createTestData') }}
				</PopconfirmButton>
			</div>
		</Col>
	</Row>
	<PaasReviewModal v-model="paasReviewModalIsVisible" :taskId="paasReviewTaskId" @applied="onCorrected" />
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import {
	Table, Space, Badge, Row, Col, Button,
	Descriptions, DescriptionsItem, Result, Spin,
	message,
} from 'ant-design-vue';
import { FieldTimeOutlined } from '@ant-design/icons-vue';

import { usePaasStore } from '@/stores/paas';
import { useSessionStore } from '@/stores/session';
import { getTodayDayJs, addDays } from '@/components/common/dateUtils';
import { showErrorMessage } from '@/components/common/errorMessage';

import { useI18n } from 'vue-i18n';

import DiscreteProgress from '@/components/common/DiscreteProgress';
import NoLineBreaks from '@/components/common/NoLineBreaks';
import StatusDisplay from '@/components/common/StatusDisplay.vue';

import PaasReviewModal from '@/components/paas/PaasReviewModal';
import PaasCoordsChart from '@/components/paas/PaasCoordsChart';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import PaasMountpointsDisplay from '@/components/paas/PaasMountpointsDisplay';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';

import ApiPaas from '@/api/ApiPaas';
import Consts from '@/api/Consts';


const PAAS_TASK_STATUS_BADGES = {
	done: 'success',
	failed: 'error',
	applied: 'success',
	declined: 'default',
};

const COLUMNS = [
	{ key: 'createdStr' },
	{ key: 'status' },
	{ key: 'mountpoints' },
	{
		dataIndex: 'id',
		key: 'actions',
	},
];


export default {
	components: {
		Table, Space, Badge, Row, Col, Button,
		Descriptions, DescriptionsItem, Result, Spin,
		FieldTimeOutlined,
		PaasMountpointsDisplay, DiscreteProgress, PaasReviewModal, PopconfirmButton,
		NoLineBreaks, StatusDisplay, PaasCoordsChart, StatsChartTitleWithDescription,
	},
	setup() {
		const { t } = useI18n();
		const isProcessing = ref(false);
		const isProcessingTestData = ref(false);
		const paasReviewModalIsVisible = ref(false);
		const paasReviewTaskId = ref(0);

		const maxDate = getTodayDayJs().toDate();
		const minDate = addDays(maxDate, Consts.BASE_STATION_PAGE_DAYS_COUNT);

		const store = usePaasStore();
		const sessionStore = useSessionStore();
		
		const paasTasks = computed(() => store.paasTasks.map((item, index) => ({
			...item,
			applyButtonType: (index === 0 && item.status === 'done') ? 'primary' : 'default',
		})));

		const onCreateTestData = () => {
			isProcessingTestData.value = true;
			ApiPaas.paasTestDataCreate(response => {
				isProcessing.value = false;
				isProcessingTestData.value = false;
				if (response.status === 'ok') {
					message.success(t('paas.messages.taskCreated'));
				} else {
					showErrorMessage(response.errors);
				}
				store.updatePaasData();
			});
		};

		const onReviewAndApply = taskId => {
			paasReviewTaskId.value = taskId;
			paasReviewModalIsVisible.value = true;
		};

		const onIgnore = taskId => {
			isProcessing.value = true;
			ApiPaas.paasTaskIgnore(taskId, () => {
				isProcessing.value = false;
				store.updatePaasData();
			});
		};

		const onCorrected = () => {
			store.updatePaasData();
		};

		onMounted(() => {
			window.scrollTo(0, 0);
			store.updatePaasData();
		});

		return {
			PAAS_TASK_STATUS_BADGES,
			columns: COLUMNS,
			isProcessing,
			isProcessingTestData,
			paasReviewModalIsVisible,
			paasReviewTaskId,
			minDate,
			maxDate,
			paasTasks,
			store,
			sessionStore,
			colorBlack: Consts.COLORS.dark0,
			onCreateTestData,
			onReviewAndApply,
			onIgnore,
			onCorrected,
		};
	},
}
</script>

<style scoped>
.h-500 {
	height: 500px;
}
</style>
